/**
 * /admin/clientes-v2 — Clientes V2 (PT-BR, R-IMPORT 1/2).
 *
 * NÃO substitui /admin/clientes (que ainda usa `clients` legacy EN, R0).
 * R-IMPORT 4 vai consolidar.
 *
 * CL1.4 (rebuild Clientes) — lista limpa: sem jargão técnico, colunas úteis
 * (razão social · CNPJ · cidade · vendedor responsável com foto · vínculo Omie).
 * Create/edit dialog + wizard + soft-delete. Buscar cliente é na BUSCA GLOBAL
 * da TopBar (Ctrl+K) — a busca local saiu em 18/08 (LIMPEZA-TELA-CLIENTES,
 * padrão BUSCA-UNICA S-4), e a global cobre nome, fantasia, CNPJ e telefone.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useUiPreference } from '@/hooks';
import { Link } from 'react-router-dom';

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErroCampo,
  HelpTip,
  Input,
  Label,
  PageContainer,
  PageHeader,
  Select,
  SelectOption,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ClientesIcon } from '@/components/icons';
import { useUsersLookup } from '@/lib/admin-store';
import { useAuth } from '@/contexts/AuthContext';
import {
  useClientesV2,
  useClientesContagem,
  useClienteV2Actions,
  type ClienteV2,
  type CreateClienteV2Input,
  SEM_ETIQUETA,
  useSegmentosClientesDisponiveis,
} from '@/lib/clientes-v2-store';
import { formatDocument } from '@/lib/format-doc';
import { AvisoConferenciaOmie } from './clientes/AvisoConferenciaOmie';
import { PrecisaDeConferenciaOmie } from './clientes/PrecisaDeConferenciaOmie';
import {
  LIMITE_RAZAO_SOCIAL_OMIE,
  mensagemRazaoLonga,
  sugerirRazaoAbreviada,
} from '@/lib/clientes/razao-social-omie';
import { ClienteFormWizard } from './ClienteFormWizard';

/** Tamanho da página. 100 é o que cabe sem rolagem interminável. */
const POR_PAGINA = 100;

/**
 * P4a (AJUSTES-POS-2): capitaliza nome de cidade em title-case pt-BR, só pra
 * EXIBIÇÃO (o dado gravado — muitas vezes em CAIXA ALTA vinda do Omie — não
 * muda). Conectivos ("de", "da", "do", "dos", "das", "e") ficam minúsculos,
 * menos quando abrem o nome. Trata separadores por espaço e hífen.
 * Ex.: "CAJAMAR" → "Cajamar"; "SAO JOSE DO RIO PRETO" → "Sao Jose do Rio Preto".
 */
const CIDADE_MINUSCULAS = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);
function capitalizarCidade(nome: string): string {
  const capPalavra = (p: string, primeira: boolean) => {
    const lower = p.toLowerCase();
    if (!primeira && CIDADE_MINUSCULAS.has(lower)) return lower;
    // Capitaliza também depois de hífen (ex.: "Mogi-Mirim").
    return lower.replace(/(^|-)([a-zà-ÿ])/g, (_m, sep, ch) => sep + ch.toUpperCase());
  };
  return nome
    .split(/\s+/)
    .map((p, i) => capPalavra(p, i === 0))
    .join(' ');
}

/**
 * CA5a (AJUSTES-POS): monta "Cidade (UF)" SEM duplicar a UF.
 *
 * O pull do Omie às vezes grava a cidade já com o estado no fim
 * (ex.: "CAJAMAR (SP)"). Anexar "(UF)" cru gerava "CAJAMAR (SP) (SP)".
 * Aqui removemos qualquer sufixo "(UF)" que já esteja na cidade (uma ou mais
 * vezes) antes de anexar a UF uma única vez. A migration 407 limpa o dado
 * gravado; este helper protege a exibição mesmo com dado sujo.
 * P4a: a cidade é exibida em title-case; a UF fica sempre em MAIÚSCULAS.
 */
function cidadeComUf(
  cidade: string | null | undefined,
  uf: string | null | undefined,
): string {
  const c = (cidade ?? '').trim();
  if (!c) return '—';
  const ufTrim = (uf ?? '').trim();
  if (!ufTrim) return capitalizarCidade(c);
  // Escapa qualquer metacaractere pra nunca quebrar o RegExp com UF atípica.
  const ufEsc = ufTrim.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\s*\\(${ufEsc}\\)\\s*$`, 'i');
  let base = c;
  while (re.test(base)) base = base.replace(re, '').trim();
  const ufUpper = ufTrim.toUpperCase();
  return base ? `${capitalizarCidade(base)} (${ufUpper})` : `(${ufUpper})`;
}

/**
 * Vínculo Omie é SÓ LEITURA: nada é escrito no Omie por esta tela.
 *
 * 🔴 **Quem decide é o servidor** (`vinculadoOmie`, vindo de
 * `omie_cliente_link` — a tabela dona). Antes a tela deduzia daqui mesmo, por
 * `source === 'omie' || !!omieId`, e `omieId` é **coluna legada** que o sync
 * não escreve mais: um cliente cadastrado à mão e vinculado depois ficava com
 * o selo cinza, vinculado.
 *
 * ⚠️ **O selo e a contagem do rodapé saem da MESMA frase**, agora em SQL
 * (`VINCULADO_OMIE_SQL`). Régua repetida em dois lugares foi o que já fez a
 * tela dizer 761 para quem enxergava 754.
 *
 * O `??` cai na régua antiga só onde o servidor não respondeu a pergunta —
 * nunca na lista, que sempre traz o campo.
 */
function isVinculadoOmie(c: ClienteV2): boolean {
  return c.vinculadoOmie ?? (c.source === 'omie' || !!c.omieId);
}

export function ClientesV2Page() {
  /**
   * Filtro por ETIQUETA (as tags que vieram do Omie: Cliente, Fornecedor,
   * Transportadora, Funcionário). `''` = todas.
   *
   * 11/08: era `useState` + filtro em memória sobre a página de 300, com 761
   * cadastros no banco — "Fornecedor" trazia os fornecedores das 300
   * primeiras linhas, não os 305, e a tela não dizia que mostrava um pedaço.
   * Agora o recorte vai ao SERVIDOR, como a busca desta tela já fazia.
   *
   * A preferência mora no backend (`useUiPreference`), não no localStorage:
   * quem trabalha em duas máquinas encontra a tela do mesmo jeito.
   */
  const [filtroSalvo, setSegmentoFiltro, apagarFiltroSalvo] = useUiPreference<string>(
    'clientes-v2.filtro-etiqueta',
    '',
  );
  /**
   * 🔴 04/10/2026 (relatado pelo Matheus com o RH ligado): o filtro SALVO podia ser uma etiqueta que não existe
   * mais entre as opções — "Funcionário" saiu de Clientes com o RH. A lista ficava vazia, a caixa mostrava
   * "Todas as etiquetas" (o navegador cai na primeira opção quando o valor não existe) e escolher "Todas" não
   * disparava mudança nenhuma. Agora: etiqueta salva que o SERVIDOR não oferece é ignorada e a preferência
   * é apagada (não volta na próxima visita). Enquanto as opções carregam, vale o salvo.
   */
  const { data: etiquetasDoServidor } = useSegmentosClientesDisponiveis();
  const filtroOrfao =
    filtroSalvo !== '' &&
    filtroSalvo !== SEM_ETIQUETA &&
    Array.isArray(etiquetasDoServidor) &&
    !etiquetasDoServidor.includes(filtroSalvo);
  const segmentoFiltro = filtroOrfao ? '' : filtroSalvo;
  useEffect(() => {
    if (filtroOrfao) apagarFiltroSalvo();
  }, [filtroOrfao, apagarFiltroSalvo]);
  /** "Limpar filtro" APAGA a preferência (não grava vazio por cima) — funciona com qualquer valor salvo. */
  const limparFiltro = () => apagarFiltroSalvo();
  const [includeInactive, setIncludeInactive] = useState(false);
  /**
   * A página atual **não é preferência**. Ela reseta a cada busca, troca de
   * etiqueta ou de "incluir inativos" — senão a pessoa volta na página 4 de
   * um recorte que agora tem 2, e vê lista vazia sem entender por quê.
   */
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClienteV2 | null>(null);
  // R-IMPORT 2.x: wizard 4-step pra cadastro (dialog antigo só pra edit)
  const [openWizard, setOpenWizard] = useState(false);

  // LIMPEZA-TELA-CLIENTES (18/08, pedido do Matheus): a busca local SAIU —
  // "assim como fizemos com as outras" = o padrão BUSCA-UNICA (S-4, decisão
  // dele em Configurações): o sistema tem UMA busca, a da TopBar (Ctrl+K).
  // A capacidade não se perdeu: a global já cobria nome/fantasia/CNPJ e
  // ganhou TELEFONE nesta rodada (busca-global.repo.ts), que era o que só a
  // busca local cobria. O `search` da API continua existindo para outros
  // chamadores; esta tela é que deixou de usá-lo.
  useEffect(() => {
    setOffset(0);
  }, [segmentoFiltro, includeInactive]);

  const { data: pagina, isLoading } = useClientesV2({
    includeInactive,
    etiqueta: segmentoFiltro || undefined,
    limit: POR_PAGINA,
    offset,
  });
  // A lista vem no envelope `{ items, total }` (padrão de Produtos). `itens`
  // é o que a tela pinta; o total do recorte vem do `useClientesContagem`.
  // `?? []` cria array NOVO a cada render, e ele é dependência de dois
  // `useMemo` abaixo — sem este memo, os dois recalculariam sempre. O lint
  // pegou; é o mesmo caso do `candidatos` no modal de unificar.
  const itens = useMemo(() => pagina?.items ?? [], [pagina]);

  const { create, update, remove } = useClienteV2Actions();
  const users = useUsersLookup();
  const vendedorPorId = useMemo(
    () => new Map(users.map((u) => [u.id, u] as const)),
    [users],
  );

  // As opções do filtro saem das tags que a LISTA CARREGADA tem — e é assim
  // que `Funcionário` some sozinho para quem não é admin: a trava de
  // privacidade já removeu esses cadastros da resposta, então a etiqueta
  // nem chega aqui. O filtro respeita a trava; não a contorna.
  //
  // ⚠️ Com um filtro ATIVO a lista traz só aquele recorte, então as demais
  // opções sumiriam do dropdown e a pessoa ficaria presa. Por isso as
  // opções são memorizadas: uma vez vistas, continuam oferecidas.
  const vistas = useRef<Set<string>>(new Set());
  const { user } = useAuth();
  const segmentosDisponiveis = useMemo(() => {
    for (const c of itens) {
      for (const t of c.tags ?? []) {
        const v = t.trim();
        // 18/08 (reversão registrada em etiqueta-visibilidade.ts): funcionário
        // é invisível para não-admin — o SERVIDOR já não manda esses cadastros
        // (nem os híbridos), então a etiqueta não chegaria; isto é o cinto de
        // segurança da UI: nem como opção morta.
        if (v && (user?.isAdmin || v !== 'Funcionário')) vistas.current.add(v);
      }
    }
    return Array.from(vistas.current).sort((a, b) =>
      a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }),
    );
  }, [itens, user?.isAdmin]);

  // O recorte por etiqueta é do SERVIDOR (11/08) e a busca local saiu
  // (LIMPEZA-TELA-CLIENTES) — a lista pinta exatamente o que a API devolve.
  // O refino em memória por nome/telefone morreu junto com o campo.
  const rows = itens;

  function handleSubmit(input: CreateClienteV2Input) {
    if (editing) {
      update.mutate(
        { id: editing.id, input },
        {
          onSuccess: () => {
            setOpen(false);
            setEditing(null);
          },
        },
      );
    } else {
      create.mutate(input, { onSuccess: () => setOpen(false) });
    }
  }

  // L3 (LAYOUT-CANONICO): o subtítulo dizia "300 clientes · 300 vinculados ao
  // Omie" porque contava o `length` da PÁGINA carregada (limit 300) e chamava
  // aquilo de total — o banco tem 763. Agora o total vem do SQL, e a tela diz
  // quantos está mostrando quando a página não cobre tudo.
  // Os MESMOS filtros da lista. Sem isto o subtítulo fala de outro universo:
  // dizia "761 clientes · mostrando 300" com o filtro de Fornecedor ativo,
  // sendo que o recorte tem 305 — e dizia 761 para o vendedor que vê 754.
  const { data: contagem } = useClientesContagem({
    includeInactive,
    etiqueta: segmentoFiltro || undefined,
  });
  const carregados = itens.length;
  const totalClientes = contagem?.total ?? carregados;
  const totalVinculados =
    contagem?.vinculadosOmie ??
    itens.filter((c) => isVinculadoOmie(c)).length;
  // "mostrando N de T", no padrão de Produtos e do audit-log do Omie. `T` é o
  // total do RECORTE (a contagem lê os mesmos filtros da lista), então a
  // navegação nunca oferece página que não existe.
  const inicio = totalClientes === 0 ? 0 : offset + 1;
  const fim = offset + carregados;
  const temAnterior = offset > 0;
  const temProxima = fim < totalClientes;
  const temFiltro = segmentoFiltro !== '';

  return (
    // B-01 (HOTFIX-ROLAGEM): tela de tabela larga usa `full`. Com o teto de
    // 1440px, em 1911px sobravam ~470px de papel em branco à direita ENQUANTO a
    // tabela rolava na horizontal e escondia a coluna "Ações" — o pior dos dois
    // mundos (regra de rolagem do CLAUDE.md).
    <PageContainer variante="full" data-testid="clientes-v2-page">
      <PageHeader
        crumb="Operacional"
        title="Clientes"
        subtitle={
          pagina
            ? `${totalClientes} ${totalClientes === 1 ? 'cliente' : 'clientes'} · ${totalVinculados} ${totalVinculados === 1 ? 'vinculado' : 'vinculados'} ao Omie` +
              (totalClientes > carregados ? ` · mostrando ${inicio}–${fim}` : '')
            : 'Cadastro central dos clientes da IGM.'
        }
        actions={
          <Button data-testid="clv2-new" onClick={() => setOpenWizard(true)}>
            + Novo cliente
          </Button>
        }
      />

      {/* #569 (13/10/2026): o aviso da conferência diária com o Omie — só administrador, só quando há divergência. */}
      <AvisoConferenciaOmie ehAdmin={!!user?.isAdmin} />
      {/* 21/10/2026: o que o Omie mudou e o sistema não gravou sozinho (outra empresa, outro código) — o admin decide. */}
      <PrecisaDeConferenciaOmie ehAdmin={!!user?.isAdmin} />

      {/* LIMPEZA-TELA-CLIENTES (18/08): a busca local que morava aqui SAIU —
          buscar cliente (nome, CNPJ ou telefone) agora é na barra do topo
          (Ctrl+K), o padrão BUSCA-UNICA aplicado em Configurações (S-4).
          Sobram os FILTROS, agrupados à esquerda; a ação principal
          ("+ Novo cliente") segue destacada no PageHeader. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-4">
        {(segmentosDisponiveis.length > 0 || segmentoFiltro) && (
          <span className="inline-flex items-center gap-1.5">
            {/* `wrapperClassName`, não `className`: a largura precisa ir no
                WRAPPER do Select (U1) — no elemento interno, o wrapper segue
                `w-full` e o chevron (absoluto no wrapper) flutua longe do
                campo. Era o "seletor vazio" que o Matheus viu ao lado do
                "Incluir inativos" (#104). */}
            <Select
              value={segmentoFiltro}
              onChange={(e) => setSegmentoFiltro(e.target.value)}
              data-testid="clv2-filtro-segmento"
              aria-label="Filtrar por etiqueta"
              wrapperClassName="w-56 max-w-full shrink-0"
            >
              <SelectOption value="">Todas as etiquetas</SelectOption>
              {/* "Tem esta etiqueta", e não "é apenas isto": um cadastro pode
                  ter várias — 80 do beta são cliente E fornecedor. O rótulo diz
                  isso para a pessoa não ler a lista como exclusiva. */}
              {segmentosDisponiveis.map((s) => (
                <SelectOption key={s} value={s}>
                  Tem: {s}
                </SelectOption>
              ))}
              {/* Os 23 sem etiqueta são justamente os que ninguém sabe o que
                  são. Sem opção própria, ficariam inalcançáveis no momento em
                  que alguém filtra — o pior destino possível para eles. */}
              <SelectOption value={SEM_ETIQUETA}>Sem etiqueta</SelectOption>
            </Select>
            <HelpTip texto="Mostra só os clientes que TÊM a etiqueta escolhida (um cadastro pode ter várias). Sem filtro, quem é só fornecedor (sem a etiqueta Cliente no Omie) não aparece: escolha “Tem: Fornecedor” para vê-lo. Para achar um cliente pelo nome, CNPJ ou telefone, use a busca no topo da tela (Ctrl+K)." />
          </span>
        )}
        <label className="flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2 cursor-pointer shrink-0">
          <Switch
            checked={includeInactive}
            onChange={() => setIncludeInactive(!includeInactive)}
          />
          Incluir inativos
          <HelpTip texto="Cliente desativado sai desta lista, mas não é apagado. Ligue para ver e reativar cadastros desativados." />
        </label>
      </div>

      <Card variant="flat">
        {isLoading ? (
          <p className="p-4 text-sm text-ink-2 dark:text-ink-dark-2">Carregando…</p>
        ) : rows.length === 0 ? (
          temFiltro ? (
            <EmptyState
              icon={<ClientesIcon className="w-6 h-6" />}
              title="Nenhum cliente encontrado"
              description="Nenhum cadastro tem a etiqueta escolhida neste recorte."
              action={
                <Button variant="secondary" onClick={limparFiltro}>
                  Limpar filtro
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<ClientesIcon className="w-6 h-6" />}
              title="Nenhum cliente ainda"
              description="Cadastre o primeiro cliente — o CNPJ preenche boa parte da ficha sozinho."
              action={<Button onClick={() => setOpenWizard(true)}>+ Novo cliente</Button>}
            />
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {/* L2 (LAYOUT-CANONICO): o CNPJ tem tamanho fixo e conhecido
                    — ganha largura própria e nunca quebra no meio
                    (`07.365.226/0001-` + `35`). Cidade idem.
                    B-01 (HOTFIX-ROLAGEM): as colunas somavam 1426px e em 1280px
                    só existem ~928px — a tabela rolava e escondia "Ações". A
                    régua agora: o que IDENTIFICA a linha (nome, CNPJ, cidade,
                    responsável) e o que AGE nela (Ativo, Ações) ficam sempre
                    visíveis; Segmento e Vínculo, que são apoio, entram a partir
                    de 1536px. O filtro de Segmento segue no topo, então o dado
                    nunca fica inalcançável. */}
                <TableHead className="min-w-0 xl:min-w-[12rem] max-w-[13rem] xl:max-w-[18rem]">Razão social</TableHead>
                {/* 🔴 AÇÃO NUNCA ATRÁS DE ROLAGEM (BUGS #163, auditoria de setembro).
                    Medido a 375px: "Editar" e "Desativar" ficavam em x=861..946 —
                    **486px além da borda**. Alcançáveis rolando, que é exatamente o
                    que a regra proíbe. ⚠️ Origem confirmada por `git log -L`:
                    26/05 e 28/07, **anteriores a agosto** — dívida antiga, não
                    regressão.
                    A saída segue o padrão que esta tela já usava (`hidden 2xl:`):
                    abaixo de `lg` some o que a ficha mostra de qualquer forma, e
                    sobra Razão social + Ações sem rolagem. */}
                <TableHead className="hidden xl:table-cell w-[9.75rem] whitespace-nowrap">CPF/CNPJ</TableHead>
                <TableHead className="hidden xl:table-cell w-[9rem]">Cidade</TableHead>
                <TableHead className="hidden 2xl:table-cell">
                  <span className="inline-flex items-center gap-1">
                    Segmento
                    <HelpTip texto="Como o cliente é classificado (ex.: Condomínio, Comércio). Vem do Omie e também pode ser ajustado na ficha do cliente. Use o filtro acima para ver só um segmento." />
                  </span>
                </TableHead>
                <TableHead className="hidden xl:table-cell w-[10rem]">Vendedor</TableHead>
                <TableHead className="hidden 2xl:table-cell">
                  <span className="inline-flex items-center gap-1">
                    Vínculo
                    <HelpTip texto="Mostra se o cliente também existe no Omie (nosso sistema fiscal/financeiro). É só informativo — nada é alterado no Omie por aqui." />
                  </span>
                </TableHead>
                <TableHead className="hidden xl:table-cell text-center">Ativo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const vendedorId = (
                  r as ClienteV2 & { vendedorResponsavelId?: string | null }
                ).vendedorResponsavelId;
                const vendedor = vendedorId
                  ? vendedorPorId.get(vendedorId)
                  : undefined;
                return (
                <TableRow key={r.id} data-testid={`clv2-row-${r.id}`}>
                  <TableCell className="min-w-0 xl:min-w-[12rem] max-w-[13rem] xl:max-w-[18rem]">
                    {/* FASE-C: nome em tinta — o vermelho IGM fica reservado
                        pra ação (427 nomes vermelhos viravam ruído).
                        L2: nome comprido trunca com reticências e mostra o
                        inteiro no `title` — nunca empurra as outras colunas. */}
                    <Link
                      to={`/admin/clientes-v2/${r.id}`}
                      title={r.razaoSocial}
                      // 🔴 O nome do cliente é O alvo desta tela, e media 22px
                      // no celular (Frente 2, 12/09/2026) — metade do piso de
                      // 44. A célula em volta tem 89px e não era clicável.
                      // ⚠️ O alvo cresce por pseudo-elemento porque o nome é
                      // uma linha de texto truncada: dar altura a ele mudaria
                      // a altura da linha em toda a tabela. Aqui isso é seguro
                      // (a célula não recorta) — ao contrário do `Tabs`, onde
                      // a tira com `overflow-x` comia os 6px prometidos.
                      className="relative block truncate font-semibold text-ink-1 dark:text-ink-dark-1 hover:text-red dark:hover:text-red-glow hover:underline before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']"
                    >
                      {r.razaoSocial}
                    </Link>
                    {r.nomeFantasia && (
                      <p
                        title={r.nomeFantasia}
                        className="truncate text-xs text-ink-3 dark:text-ink-dark-3"
                      >
                        {r.nomeFantasia}
                      </p>
                    )}
                    {/* 13/08: cadastro pela metade visível DA LISTA — mesma
                        régua da travaGanho (documento + completude marcada).
                        O title diz O QUE falta; a ficha é onde se resolve. */}
                    {(r.cadastroFaltas?.length ?? 0) > 0 && (
                      <Badge
                        variant="warning"
                        size="sm"
                        data-testid={`clv2-incompleto-${r.id}`}
                        title={`Falta: ${(r.cadastroFaltas ?? []).join(' · ')}`}
                      >
                        incompleto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell whitespace-nowrap">
                    {/* 04/10: o CPF mora na coluna `cnpj` (é assim que a busca do Omie grava) — `formatDocument` decide pelo
                        tamanho; com `formatCNPJ` todo CPF virava "—". */}
                    <code className="text-xs">{r.cnpj ? formatDocument(r.cnpj) : '—'}</code>
                  </TableCell>
                  {/* B-01: `nowrap` fazia a coluna crescer até caber
                      "SAO JOSE DO RIO PRETO (SP)" — 222px. Truncar mantém a
                      promessa do L2 (não QUEBRA no meio) sem empurrar a tabela
                      para fora da tela; o nome inteiro fica no `title`. */}
                  <TableCell className="hidden xl:table-cell max-w-[9rem]">
                    <span
                      className="block truncate"
                      title={cidadeComUf(r.enderecoCidade, r.enderecoUf)}
                    >
                      {cidadeComUf(r.enderecoCidade, r.enderecoUf)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden 2xl:table-cell">
                    {/* FASE-C: segmento é classificação, não ação — badge
                        neutro (o vermelho competia com os botões).
                        B-01: acompanha o cabeçalho — some abaixo de 1536px. */}
                    {(r.tags ?? []).length > 0 ? (
                      <span className="inline-flex flex-wrap gap-1">
                        {(r.tags ?? []).map((t) => (
                          <Badge key={t} variant="neutral" size="sm">
                            {t}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      <span className="text-ink-3 dark:text-ink-dark-3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell max-w-[10rem]">
                    {vendedor ? (
                      <span className="flex items-center gap-2 min-w-0" title={vendedor.nome}>
                        <Avatar name={vendedor.nome} src={vendedor.fotoUrl} size="xs" />
                        <span className="truncate text-sm">{vendedor.nome}</span>
                      </span>
                    ) : (
                      <span className="text-ink-3 dark:text-ink-dark-3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden 2xl:table-cell">
                    {isVinculadoOmie(r) ? (
                      <Badge variant="success" size="sm">
                        Omie vinculado
                      </Badge>
                    ) : (
                      <span className="text-ink-3 dark:text-ink-dark-3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell text-center">
                    <Switch
                      checked={r.ativo}
                      onChange={() => update.mutate({ id: r.id, input: { ativo: !r.ativo } })}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(r);
                        setOpen(true);
                      }}
                    >
                      Editar
                    </Button>
                    {r.ativo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red"
                        onClick={() => {
                          if (confirm(`Desativar "${r.razaoSocial}"?`)) {
                            remove.mutate(r.id);
                          }
                        }}
                      >
                        Desativar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Navegação — mesmo desenho de Produtos e do audit-log do Omie: dois
          botões, desabilitados nas bordas, e a faixa dita em texto. Só aparece
          quando há mais de uma página: com 12 clientes, dois botões cinzas são
          ruído. */}
      {totalClientes > carregados || offset > 0 ? (
        <div className="flex flex-wrap items-center gap-2 pt-3" data-testid="clv2-paginacao">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
            disabled={!temAnterior}
            data-testid="clv2-anteriores"
          >
            Anteriores
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + POR_PAGINA)}
            disabled={!temProxima}
            data-testid="clv2-proximos"
          >
            Próximos
          </Button>
          <span className="text-sm text-ink-3 dark:text-ink-dark-3">
            {inicio}–{fim} de {totalClientes}
          </span>
        </div>
      ) : null}

      <ClienteV2Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        editing={editing}
        onSubmit={handleSubmit}
        busy={create.isPending || update.isPending}
        errorMsg={create.error?.message ?? update.error?.message}
      />

      <ClienteFormWizard
        open={openWizard}
        onClose={() => setOpenWizard(false)}
      />
    </PageContainer>
  );
}

// `export` só pelo teste do limite de 60 (razao-social-60.test.tsx): renderizar
// a página inteira exigiria mockar meia dúzia de hooks pra chegar neste dialog.
export function ClienteV2Dialog({
  open,
  onClose,
  editing,
  onSubmit,
  busy,
  errorMsg,
}: {
  open: boolean;
  onClose: () => void;
  editing: ClienteV2 | null;
  onSubmit: (input: CreateClienteV2Input) => void;
  busy?: boolean;
  errorMsg?: string;
}) {
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [enderecoLogradouro, setEnderecoLogradouro] = useState('');
  const [enderecoNumero, setEnderecoNumero] = useState('');
  const [enderecoBairro, setEnderecoBairro] = useState('');
  const [enderecoCidade, setEnderecoCidade] = useState('');
  const [enderecoUf, setEnderecoUf] = useState('');
  const [enderecoCep, setEnderecoCep] = useState('');

  useMemo(() => {
    if (open) {
      setRazaoSocial(editing?.razaoSocial ?? '');
      setNomeFantasia(editing?.nomeFantasia ?? '');
      setCnpj(editing?.cnpj ?? '');
      setSegmentId(editing?.segmentId ?? '');
      setEmail(editing?.email ?? '');
      setTelefone(editing?.telefone ?? '');
      setEnderecoLogradouro(editing?.enderecoLogradouro ?? '');
      setEnderecoNumero(editing?.enderecoNumero ?? '');
      setEnderecoBairro(editing?.enderecoBairro ?? '');
      setEnderecoCidade(editing?.enderecoCidade ?? '');
      setEnderecoUf(editing?.enderecoUf ?? '');
      setEnderecoCep(editing?.enderecoCep ?? '');
    }
  }, [open, editing]);

  // Cliente terceirizado deixou de existir (01/08): a IGM cobra sempre direto do
  // condomínio. A validação era `tipo === 'direto' || empresaContratanteId…` —
  // com o tipo sempre direto, sobra só a razão social.
  //
  // Limite do Omie (15/08): >60 barra — mas SÓ quando a razão foi editada.
  // Ficha antiga com nome longo salvando outro campo passa (o dialog manda o
  // form inteiro; a comparação com o valor de antes é o que evita a trava).
  // Mesma regra do servidor (erroRazaoNoUpdate) — a tela espelha, não decide.
  const razaoTrim = razaoSocial.trim();
  const razaoLonga =
    razaoTrim.length > LIMITE_RAZAO_SOCIAL_OMIE &&
    razaoTrim !== (editing?.razaoSocial ?? '').trim();
  const razaoSugestao = razaoLonga ? sugerirRazaoAbreviada(razaoTrim) : null;
  const valid = razaoTrim.length >= 2 && !razaoLonga;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>
          {editing ? `Editar: ${editing.razaoSocial}` : 'Novo cliente'}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-4">
          {errorMsg && (
            <Alert variant="danger" rail>
              {errorMsg}
            </Alert>
          )}

          {/* O seletor "cliente direto × via empresa terceirizada" e o select de
              empresa contratante saíram em 01/08: a IGM não atende por meio de
              administradoras, toda cobrança é direta com o condomínio. Todo
              cliente é direto — o par de rádios não oferecia escolha real (0 de
              760 clientes estavam marcados como terceirizados). */}

          <div>
            <span className="inline-flex items-center gap-1.5">
              <Label htmlFor="clv2-razao" required>
                Razão social
              </Label>
              <HelpTip texto="Limite de 60 caracteres: o Omie, onde o cadastro é faturado, não aceita mais que isso. Se o nome vier maior, encurte mantendo o que identifica o cliente — o sistema sugere a abreviação no padrão da casa." />
            </span>
            <Input
              id="clv2-razao"
              data-testid="clv2-form-razao"
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
              aria-invalid={razaoLonga}
            />
            {/* Limite do Omie (15/08): contador sempre visível; nunca trunca —
                o valor fica inteiro na tela e o salvar é que barra. Cadastro
                antigo com a razão intocada NÃO barra (razaoLonga compara). */}
            <p
              className={`text-xs mt-0.5 ${razaoTrim.length > LIMITE_RAZAO_SOCIAL_OMIE ? 'text-danger font-medium' : 'text-ink-3 dark:text-ink-dark-3'}`}
              data-testid="clv2-razao-contador"
            >
              {razaoTrim.length}/{LIMITE_RAZAO_SOCIAL_OMIE}
            </p>
            <ErroCampo
              id="clv2-razao"
              mensagem={razaoLonga ? mensagemRazaoLonga(razaoTrim.length) : undefined}
            />
            {razaoSugestao && (
              <Button
                size="sm"
                variant="secondary"
                className="mt-1.5"
                data-testid="clv2-razao-sugestao"
                onClick={() => setRazaoSocial(razaoSugestao)}
              >
                Usar sugestão: {razaoSugestao}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="clv2-fantasia">Nome fantasia</Label>
              <Input
                id="clv2-fantasia"
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="clv2-cnpj">CNPJ</Label>
              <Input
                id="clv2-cnpj"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0001-00"
              />
            </div>
            {/*
              CA2 (AJUSTES-POS): o "Segmento" único (client_segments/segment_id)
              foi APOSENTADO da UI. O Segmento agora é a lista multivalor de tags
              (vem do Omie + manual), gerenciada na ficha do cliente. O valor
              legado de segment_id é preservado no save (round-trip), só não é
              mais editável aqui.
            */}
          </div>

          <div className="border-t border-line dark:border-line-dark pt-4">
            <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-3">
              Contato
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="clv2-email">E-mail</Label>
                <Input
                  id="clv2-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="clv2-tel">Telefone</Label>
                <Input
                  id="clv2-tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-line dark:border-line-dark pt-4">
            <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-3">
              Endereço fiscal
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-3">
                <Label htmlFor="clv2-log">Logradouro</Label>
                <Input
                  id="clv2-log"
                  value={enderecoLogradouro}
                  onChange={(e) => setEnderecoLogradouro(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="clv2-num">Número</Label>
                <Input
                  id="clv2-num"
                  value={enderecoNumero}
                  onChange={(e) => setEnderecoNumero(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="clv2-bai">Bairro</Label>
                <Input
                  id="clv2-bai"
                  value={enderecoBairro}
                  onChange={(e) => setEnderecoBairro(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="clv2-cid">Cidade</Label>
                <Input
                  id="clv2-cid"
                  value={enderecoCidade}
                  onChange={(e) => setEnderecoCidade(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="clv2-uf">UF</Label>
                <Input
                  id="clv2-uf"
                  value={enderecoUf}
                  onChange={(e) => setEnderecoUf(e.target.value)}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
              <div>
                <Label htmlFor="clv2-cep">CEP</Label>
                <Input
                  id="clv2-cep"
                  value={enderecoCep}
                  onChange={(e) => setEnderecoCep(e.target.value)}
                  placeholder="00000-000"
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          data-testid="clv2-form-submit"
          loading={busy}
          disabled={!valid}
          onClick={() =>
            onSubmit({
              razaoSocial: razaoSocial.trim(),
              nomeFantasia: nomeFantasia.trim() || null,
              cnpj: cnpj.trim() || null,
              segmentId: segmentId || null,
              email: email.trim() || null,
              telefone: telefone.trim() || null,
              enderecoLogradouro: enderecoLogradouro.trim() || null,
              enderecoNumero: enderecoNumero.trim() || null,
              enderecoBairro: enderecoBairro.trim() || null,
              enderecoCidade: enderecoCidade.trim() || null,
              enderecoUf: enderecoUf.trim() || null,
              enderecoCep: enderecoCep.trim() || null,
            })
          }
        >
          {editing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
